import React from 'react';
import {useNavigate} from 'react-router-dom';
import {Col, DatePicker, Row, Spin, Typography} from 'antd';
import withDragAndDrop, {EventInteractionArgs, OnDragStartArgs} from 'react-big-calendar/lib/addons/dragAndDrop';
import {Calendar, EventPropGetter, momentLocalizer, SlotInfo, Views} from 'react-big-calendar';
import moment from 'moment';
import dayjs, {Dayjs} from 'dayjs';

import {
    BookingDeploymentWithBooking,
    BookingEvent,
    BookingType,
    CalenderViewProps,
    Deployment,
    DeploymentBooking,
    EventInfo,
    NewBookingEventData,
    Resource
} from '../../../types/bookingTypes';
import BookingCreateForm from '../../../components/booking/BookingCreateForm';
import {bookingInfo, getBookingIncrement, getMinBookingLength, roundToBookingIncrement} from '../../../helpers/booking';
import {useAppSelector} from '../../../hooks/useStore';
import {selectAccountMembershipNumber, selectIsStaff} from '../../../store/slices/userSlice';
import {dateFormat, dayNameDateFormat} from '../../../lib/time';
import {messageType, showMessages} from '../../../helpers/message';
import {
    availabilityHasChangedAndYourSelectionIsNoLongerValid,
    bookingOverlapAnotherBooking,
    invalidActionPleaseTryAgain,
    newBookingsCannotBeInThePastOrOverlapExistingBookings
} from '../../../helpers/text';
import {useLazyGetDeploymentBookingsQuery} from '../../../store/api/bookingsApi';

import 'react-big-calendar/lib/css/react-big-calendar.css';
import './CalendarView.less';
import {bookingDatePickerLabel, formItemFullWidth, redAsterisk, titleHeader} from '../../../assets/styles';
import {BaseError} from '../../../types/apiTypes';

const DraggableCalendar = withDragAndDrop(Calendar);

// When using dayjs localizer the booking grid SIGNIFICANTLY slows down to a crawl.
// So keep moment for this one file in the application.
const localizer = momentLocalizer(moment);

/**
 * This component handles calendar related events & resources for display
 * and update booking data when user makes changes using booking form.
 *
 * Calendar terminology:
 * Events are events to display;
 * Resources are deployments (as columns) on the calendar.
 *
 * Note: The one and only source for new booking data should be BookingGridView, not from local events state.
 * Once data is updated in BookingGridView, events state will be updated accordingly within ComponentDidUpdate.
 */
const CalendarView = ({
                          deployments,
                          buildBookingData,
                          clearNewBooking,
                          newBooking,
                          newBookingTitle,
                          goToBookingSummary,
                          itemsPerPage
                      }: CalenderViewProps) => {

    const navigate = useNavigate();
    const isStaff = useAppSelector(selectIsStaff);
    const accountMembershipNumber = useAppSelector(selectAccountMembershipNumber);
    const membershipID = useAppSelector(state => state.user.membership)?.id;

    const [getDeploymentBookings] = useLazyGetDeploymentBookingsQuery();

    const [deploymentBookings, setDeploymentBookings] = React.useState<DeploymentBooking[] | undefined>(undefined);
    const [events, setEvents] = React.useState<BookingEvent[]>([]);
    const [resources, setResources] = React.useState<Resource[]>([]);
    const [startTime, setStartTime] = React.useState<Dayjs | null>(null);
    const [endTime, setEndTime] = React.useState<Dayjs | null>(null);
    const [bookingType, setBookingType] = React.useState<BookingType>('set_return');
    const [calendarDate, setCalendarDate] = React.useState<Dayjs | undefined>(undefined);
    const [resourceID, setResourceID] = React.useState<number | null>(null);
    const [isUpdatingBooking, setIsUpdatingBooking] = React.useState<boolean>(false);
    const [carInfo, setCarInfo] = React.useState<string>('');
    const [locationInfo, setLocationInfo] = React.useState<string>('');
    const [loading, setLoading] = React.useState<boolean>(true);

    const mounted = React.useRef(false);

    const minBookingLengthMinutes = Number(getMinBookingLength()) / 60;
    const bookingIncrementMinutes = Number(getBookingIncrement()) / 60;
    const formats = {
        dayHeaderFormat: dayNameDateFormat,
    }

    //region Calendar Resources and States Helpers.

    const getDeploymentIDFromResourceID = (resourceID: number) => {
        const resource = resources.find(resource => resource.resourceID === resourceID);
        return resource ? resource.resourceDeploymentID : 0;
    };

    const getResourceIDFromDeploymentID = (resources: Resource[], deploymentID: number) => {
        const resource = resources.find(resource => resource.resourceDeploymentID === deploymentID);
        return resource ? resource.resourceID : 0;
    };

    const constructBookingEvent = ({
                                       id,
                                       type,
                                       start,
                                       end,
                                       bookingType,
                                       bookingID,
                                       resourceID,
                                       title,
                                       accountMembershipNumber
                                   }: Partial<BookingEvent>): BookingEvent | undefined => {
        if (id === undefined) return undefined;

        switch (type) {
            case bookingInfo.type.new.internalName:
                return {
                    id,
                    type,
                    draggable: true,
                    resizable: true,
                    title: title ?? '',
                    start: start || dayjs().tz(),
                    end: end || dayjs().tz(),
                    bookingType: bookingType!,
                    allDay: false,
                    className: 'new-booking-style',
                    bookingID: bookingID ?? 0,
                    resourceID: resourceID ?? 0,
                    accountMembershipNumber: accountMembershipNumber ?? '',
                };
            case bookingInfo.type.existing.internalName:
                return {
                    id,
                    type,
                    draggable: false,
                    resizable: false,
                    allDay: false,
                    title: title ?? '',
                    start: start || dayjs().tz(),
                    end: end || dayjs().tz(),
                    bookingType: bookingType!,
                    bookingID: bookingID ?? 0,
                    resourceID: resourceID ?? 0,
                    accountMembershipNumber: accountMembershipNumber ?? '',
                };
            default:
                return undefined;
        }
    };

    const createOrUpdateNewBookingEventInStates = React.useCallback(({
                                                                         id,
                                                                         start,
                                                                         end,
                                                                         bookingType,
                                                                         resourceID
                                                                     }: Partial<BookingEvent>) => {
        const newEvents: BookingEvent[] = events;
        const existingEventIndex = events.findIndex(event => event.type === bookingInfo.type.new.internalName);

        // Remove existing newBooking event if there is any.
        if (existingEventIndex !== -1) newEvents.splice(existingEventIndex, 1);

        let title: string | undefined = bookingInfo.type.new.displayName;
        if (isStaff) title = newBookingTitle;

        const event = constructBookingEvent({
            id,
            type: bookingInfo.type.new.internalName,
            start,
            end,
            bookingType,
            bookingID: undefined,
            resourceID,
            title,
            accountMembershipNumber: accountMembershipNumber ?? "",
        });
        if (event) newEvents.push(event);

        setEvents(newEvents);
    }, [events, newBookingTitle, isStaff, accountMembershipNumber]);

    const getCarAndLocationInfo = React.useCallback((resourceID: number) => {
        if (resources?.length !== 0 && resourceID !== 0) {
            const resource = resources.find(resource => resource.resourceID === resourceID);
            return [resource?.deployableDescription ?? '', resource?.locationDescription ?? ''];
        }
        return ['', ''];
    }, [resources]);

    //endregion

    //region Calendar Display Helpers

    const getMinStart = () => {
        return roundToBookingIncrement(dayjs().tz(), 'down');
    };

    // Returns false if the current membership has any overlapping bookings in this time-slot, otherwise returns true.
    const selectionIsValid = React.useCallback((startDate: Dayjs, endDate: Dayjs) => {
        const minStart = getMinStart().toDate();

        if (!isStaff && startDate < minStart) return false;

        return !events.some(event =>
            event.type !== bookingInfo.type.new.internalName &&
            event.accountMembershipNumber === accountMembershipNumber &&
            (
                (startDate >= event.start && endDate <= event.end) ||
                (endDate >= event.end && startDate < event.end) ||
                (startDate < event.start && endDate > event.start)
            )
        );
    }, [events, isStaff, accountMembershipNumber]);

    const displayBookingWarningMessage = React.useCallback((warningType: string) => {
        let warningMessage;
        switch (warningType) {
            case bookingInfo.warning.invalidSelection: {
                warningMessage = bookingOverlapAnotherBooking;
                break;
            }
            default:
                break;
        }

        showMessages(warningMessage, messageType.warn);
    }, [])

    const displayBookingErrorMessage = React.useCallback((errorType: string) => {
        let errorMessage;

        switch (errorType) {
            case bookingInfo.error.invalidSelection: {
                errorMessage = newBookingsCannotBeInThePastOrOverlapExistingBookings;
                break;
            }
            case bookingInfo.error.availabilityChange: {
                errorMessage = availabilityHasChangedAndYourSelectionIsNoLongerValid;
                break;
            }
            case bookingInfo.error.invalidAction: {
                errorMessage = invalidActionPleaseTryAgain;
                break;
            }
            default:
                break;
        }

        showMessages(errorMessage, messageType.error);
    }, []);

    //endregion

    //region Calendar Lifecycle Helpers

    // Note: events ID can start from 0, but resource ID has to start from 1 as the calendar expects so.
    const populateExistingBookings = React.useCallback((deploymentBookings: BookingDeploymentWithBooking[], resourceID: number, existingEventsCount: number) => {
        if (!deploymentBookings || deploymentBookings.length === 0) {
            return [];
        }

        const newEvents: BookingEvent[] = deploymentBookings.map((event, index) => {
            const startDate = dayjs(event.started_at).tz().toDate();
            const endDate = event.ended_at === null ? dayjs(event.started_at).tz().add(1, 'year').toDate() : dayjs(event.ended_at).tz().toDate();
            let title = bookingInfo.type.existing.displayName;
            if (isStaff) {
                title = `${event.account_membership_number} ${event.person_name}, ${event.company_name}`;
            }
            return constructBookingEvent({
                id: existingEventsCount + index,
                type: bookingInfo.type.existing.internalName,
                start: startDate,
                end: endDate,
                bookingID: event.booking_id ?? 0,
                // Staff side will have booking_id, default to 0 for members as they don't have access to clicking on grid events.
                resourceID,
                title,
                accountMembershipNumber: event.account_membership_number ?? '',
            })!;
        });
        return newEvents;
    }, [isStaff]);

    const populateUnavailableDeploymentBookings = React.useCallback((deployment: Deployment, resourceID: number, existingEventsCount: number) => {
        const newEvents: BookingEvent[] = [];

        const deploymentStartDate = dayjs(deployment.started_at).tz();
        // Hard-code 1 year prior to deployment start as unavailability start date.
        const unavailabilityStartDate = deploymentStartDate.subtract(1, 'year');
        const title = bookingInfo.type.existing.displayName;

        const preDeploymentBooking: BookingEvent = constructBookingEvent({
            id: existingEventsCount,
            type: bookingInfo.type.existing.internalName,
            start: unavailabilityStartDate.toDate(),
            end: deploymentStartDate.toDate(),
            bookingID: undefined,
            resourceID,
            title,
        })!;

        newEvents.push(preDeploymentBooking);

        if (deployment.ended_at !== null) {
            const deploymentEndDate = dayjs(deployment.ended_at).tz();
            const unavailabilityEndDate = deploymentEndDate.add(1, 'year');

            const postDeploymentBooking: BookingEvent = constructBookingEvent({
                id: existingEventsCount + 1,
                type: bookingInfo.type.existing.internalName,
                start: deploymentEndDate.toDate(),
                end: unavailabilityEndDate.toDate(),
                bookingID: undefined,
                resourceID,
                title,
            })!;

            newEvents.push(postDeploymentBooking);
        }

        return newEvents;
    }, []);

    // Note: events ID can start from 0, but resource ID has to start from 1 as the calendar expects so.
    const setData = React.useCallback((deployments: Deployment[], deploymentBookings: DeploymentBooking[]) => {
        const newEvents: BookingEvent[] = [];
        const resourceMap: Resource[] = [];

        deployments.forEach((deployment, deploymentIndex) => {
            const bookingDeployments = deploymentBookings[deploymentIndex]?.booking_deployments;
            const newResourceID = deploymentIndex + 1;

            // Populate resourceMap for linking events with resource.
            resourceMap.push({
                resourceID: newResourceID,
                resourceDeploymentID: deployment.id,
                deployableDescription: deployment.deployable_description,
                locationDescription: deployment.location_description,
                resourceTitle: `${deployment.deployable_description} @ ${deployment.location_description}`,
            });

            // Populate existing bookings.
            const existingBookingsEvents = populateExistingBookings(bookingDeployments, newResourceID, newEvents.length);
            newEvents.push(...existingBookingsEvents);

            // Populate unavailable deployments for car moves.
            const unavailableDeploymentEvents = populateUnavailableDeploymentBookings(deployment, newResourceID, newEvents.length);
            newEvents.push(...unavailableDeploymentEvents);
        });

        setEvents(newEvents);
        setResources(resourceMap);
        setLoading(false);
    }, [populateExistingBookings, populateUnavailableDeploymentBookings]);

    const updateNewBookingEvent = React.useCallback(({
                                                         newStartDate,
                                                         newEndDate,
                                                         newStartTime,
                                                         newEndTime,
                                                         newBookingType,
                                                         newResourceID,
                                                         numberOfEvents
                                                     }: NewBookingEventData) => {
        const isValid = selectionIsValid(newStartDate, newEndDate);

        if (isValid || isStaff) {
            const [carInfo, locationInfo] = getCarAndLocationInfo(newResourceID);

            createOrUpdateNewBookingEventInStates({
                id: numberOfEvents,
                start: newStartDate.toDate(),
                end: newEndDate.toDate(),
                bookingType: newBookingType,
                resourceID: newResourceID,
            });

            setStartTime(newStartTime);
            setEndTime(newEndTime);
            setBookingType(newBookingType);
            setResourceID(newResourceID);
            setCarInfo(carInfo);
            setLocationInfo(locationInfo);
            setCalendarDate(newStartDate);
        } else {
            clearNewBooking();
            displayBookingErrorMessage(bookingInfo.error.availabilityChange);
        }
    }, [isStaff, clearNewBooking, selectionIsValid, createOrUpdateNewBookingEventInStates, getCarAndLocationInfo,
        displayBookingErrorMessage]);

    //endregion

    //region Booking Form

    const syncStateWithBookingForm = ({startTime, endTime, bookingType}: {
        startTime: Dayjs,
        endTime: Dayjs,
        bookingType: BookingType
    }) => {
        const isValid = selectionIsValid(startTime, endTime);
        if (isValid || isStaff) {
            if (!isValid) displayBookingWarningMessage(bookingInfo.warning.invalidSelection);

            setStartTime(startTime);
            setEndTime(endTime);
            setBookingType(bookingType);

            const deploymentID = resourceID ? getDeploymentIDFromResourceID(resourceID) : 0;

            buildBookingData({
                startedAt: startTime,
                endedAt: endTime,
                deploymentID,
                carInfo,
                locationInfo,
                bookingType,
                membershipID,
            });
            return true;
        } else {
            displayBookingErrorMessage(bookingInfo.error.invalidSelection);
            return false;
        }
    }

    const toggleFormVisibility = (value: boolean) => setIsUpdatingBooking(value);

    //endregion

    //region Calendar Event

    const calendarSelectEventCallback = (event: object, e: React.SyntheticEvent<HTMLElement, Event>) => {
        const eventInfo = event as EventInfo;

        if (eventInfo.type === bookingInfo.type.new.internalName) {
            setIsUpdatingBooking(true);
        }

        if (isStaff && eventInfo.bookingID && eventInfo.bookingID !== 0) {
            navigate(`/booking/${eventInfo.bookingID}`);
        }
    };

    const calendarSelectSlotCallback = (slotInfo: SlotInfo) => {
        let startTime, endTime;

        const resourceID = Number(slotInfo.resourceId ?? 0);
        const deploymentID = getDeploymentIDFromResourceID(resourceID);
        const minBookingLength = minBookingLengthMinutes;
        const bookingType = 'set_return'; // Default booking type for new/reselection.

        // Handle different actions.
        switch (slotInfo.action) {
            case 'click':
            case 'doubleClick':
                startTime = dayjs(slotInfo.start).tz();
                endTime = startTime.add(minBookingLength, "minutes");
                break;
            case 'select':
                startTime = dayjs(slotInfo.start).tz();
                endTime = dayjs(slotInfo.end).tz();

                if (endTime.hour() === 23 && endTime.minute() === 59) {
                    endTime = endTime.add(1, 'minutes');
                }

                // Ensure minimum booking length
                if (endTime.diff(startTime, 'minutes') < minBookingLength) {
                    endTime = startTime.add(minBookingLength, "minutes").tz();
                }

                if (newBooking) {
                    newBooking.startedAt = startTime;
                    newBooking.endedAt = endTime;
                }
                break;
            default:
                displayBookingErrorMessage(bookingInfo.error.invalidAction);
                return;
        }

        const isValid = selectionIsValid(startTime, endTime);
        if (isValid || isStaff) {
            if (!isValid) displayBookingWarningMessage(bookingInfo.warning.invalidSelection);

            const [carInfo, locationInfo] = getCarAndLocationInfo(resourceID);

            setStartTime(startTime);
            setEndTime(endTime);
            setBookingType(bookingType);
            setResourceID(resourceID);
            setCarInfo(carInfo);
            setLocationInfo(locationInfo);
            setIsUpdatingBooking(true);

            // Bubble up with start and end info.
            buildBookingData({
                startedAt: startTime,
                endedAt: endTime,
                deploymentID,
                bookingType,
                carInfo,
                locationInfo,
                membershipID,
            });

        } else {
            displayBookingErrorMessage(bookingInfo.error.invalidSelection);
        }
    };

    // Note that this does nothing at the moment! It just needs to be defined.
    const calendarEventDragStartCallback = (_: OnDragStartArgs<object>) => {
    };

    const calendarEventDropCallback = (args: EventInteractionArgs<object>) => {
        const {event} = args;
        const eventInfo = event as EventInfo;

        const resourceID = Number(eventInfo.resourceID ?? 0);
        const newStartTime = dayjs(eventInfo.start).tz();
        const newEndTime = dayjs(eventInfo.end).tz();

        if (selectionIsValid(newStartTime, newEndTime)) {
            const [carInfo, locationInfo] = getCarAndLocationInfo(resourceID);

            setStartTime(newStartTime);
            setEndTime(newEndTime);
            setCarInfo(carInfo);
            setLocationInfo(locationInfo);

            const deploymentID = getDeploymentIDFromResourceID(resourceID);

            // Bubble up with start and end info.
            buildBookingData({
                startedAt: newStartTime,
                endedAt: newEndTime,
                deploymentID,
                carInfo,
                locationInfo,
                bookingType,
                membershipID,
            });
        } else {
            displayBookingErrorMessage(bookingInfo.error.invalidSelection);
        }
    };

    const calendarEventResizeCallback = (_: EventInteractionArgs<object>) => {
    };

    // Sets the styling for booking blocks on the calendar based on event properties or default styling for staff or members.
    const eventPropGetter: EventPropGetter<object> = (event: object, start: Date, end: Date, isSelected: boolean) => {
        const eventInfo = event as EventInfo;

        if (eventInfo.style !== undefined) {
            return {style: eventInfo.style};
        } else if (eventInfo.className !== undefined) {
            return {className: eventInfo.className};
        }

        // Apply default style based on user type.
        const defaultClassName = isStaff ? 'default-booking-style-staff' : 'default-booking-style-member';
        return {className: defaultClassName};
    };

    const onDateChange = async (date: Date | Dayjs) => {
        date = dayjs(dayjs(date).tz().startOf('day').format('YYYY-MM-DD HH:mm:ssZZ'));
        setCalendarDate(date);
        await populateExistingBookingDeployments(deployments, date);
    }

    //endregion

    const populateExistingBookingDeployments = React.useCallback(async (deployments: Deployment[], curDate: Dayjs, pageNumber = 1) => {
        try {
            const deploymentIDString = buildDeploymentIDString(deployments);

            const response = await getDeploymentBookings({
                deploymentIDs: deploymentIDString,
                bookingStatus: 'CONFIRMED',
                pageSize: itemsPerPage,
                pageNumber,
                date: curDate,
            }).unwrap();

            if (response) {
                setDeploymentBookings(response);
            }
        } catch (error) {
            showMessages((error as BaseError).data?.message, messageType.error);
        }
    }, [getDeploymentBookings, itemsPerPage]);

    const buildDeploymentIDString = (deployments: Deployment[]) => {
        return deployments.map(deployment => deployment.id).join(',');
    };

    //region Lifecycle

    // Populate booking deployments for current date. Behaves like an onMount.
    React.useEffect(() => {
        if (!mounted.current) {
            const date = dayjs(dayjs().tz().startOf('day').format('YYYY-MM-DD HH:mm:ssZZ'));
            populateExistingBookingDeployments(deployments, date);
            mounted.current = true;
        }
    }, [populateExistingBookingDeployments, deployments]);

    React.useEffect(() => {
        if (deployments && deploymentBookings && deploymentBookings.length !== 0) {
            setData(deployments, deploymentBookings);
        }
    }, [deployments, deploymentBookings, setData]);

    React.useEffect(() => {
        // populate new booking.
        if (newBooking !== undefined && resources.length !== 0) {
            setStartTime(newBooking.startedAt);
            setEndTime(newBooking.endedAt);
            setBookingType(newBooking.bookingType);

            const deploymentID = newBooking.deploymentID;
            const newResourceID = getResourceIDFromDeploymentID(resources, deploymentID);
            const [carInfo, locationInfo] = getCarAndLocationInfo(newResourceID);

            setResourceID(newResourceID);
            setCarInfo(carInfo);
            setLocationInfo(locationInfo);
            setCalendarDate(dayjs(newBooking.startedAt).tz());
        }
    }, [newBooking, resources, getCarAndLocationInfo]);

    React.useEffect(() => {
        const newBookingEventInState = events.find(event => event.type === bookingInfo.type.new.internalName);

        // Once resources are updated, we can display/update newBooking if it exists
        // Relevant when click to create new booking and coming back from the booking summary.
        if (newBooking !== undefined && resources.length !== 0) {
            // If event start/end time or resourceID is not up-to-date with newBooking in props, update the event.
            if (newBookingEventInState === undefined ||
                (newBookingEventInState && !dayjs(newBookingEventInState.start).tz().isSame(newBooking.startedAt)) ||
                (newBookingEventInState && !dayjs(newBookingEventInState.end).tz().isSame(newBooking.endedAt)) ||
                (newBookingEventInState && resourceID !== newBookingEventInState.resourceID)
            ) {
                const deploymentID = newBooking.deploymentID;
                const newResourceID = getResourceIDFromDeploymentID(resources, deploymentID);
                const newStartTime = dayjs(newBooking.startedAt).tz();
                const newEndTime = dayjs(newBooking.endedAt).tz();
                const newBookingType = newBooking.bookingType;
                const newStartDate = dayjs(newStartTime.toDate()).tz();
                const newEndDate = dayjs(newEndTime.toDate()).tz();

                updateNewBookingEvent({
                    newStartDate,
                    newEndDate,
                    newStartTime,
                    newEndTime,
                    newBookingType,
                    newResourceID,
                    numberOfEvents: events?.length,
                });
            }
        }
    }, [newBooking, resourceID, resources, events, setData, updateNewBookingEvent]);

    //endregion

    return (
        <>
            {loading ? <Spin/> : (
                <div className="calendar-container">
                    <Row key="content" justify="start" style={{...titleHeader, height: '100%'}}>
                        <Col style={{...bookingDatePickerLabel}}>
                            <Typography.Text style={{...redAsterisk}}>*</Typography.Text>
                            <Typography.Text>Select a date: </Typography.Text>
                        </Col>
                        <Col xs={24} sm={6} md={6} lg={4}>
                            <DatePicker
                                placeholder="YYYY-MM-DD"
                                format={dateFormat}
                                allowClear={false}
                                defaultValue={dayjs().tz().startOf('day')}
                                value={dayjs(calendarDate).tz()}
                                onChange={(e) => onDateChange(e as Dayjs)}
                                style={{...formItemFullWidth}}
                            />
                        </Col>
                    </Row>
                    <DraggableCalendar
                        selectable
                        resizable
                        showMultiDayTimes

                        localizer={localizer}
                        views={['day']}
                        defaultView={Views.DAY}
                        formats={formats}
                        defaultDate={dayjs().tz().toDate()}
                        date={dayjs(calendarDate).tz().toDate() || dayjs().tz().toDate()}
                        onNavigate={(newDate: Date) => onDateChange(dayjs(newDate))}

                        step={bookingIncrementMinutes}
                        timeslots={2}
                        events={events}
                        resources={resources}

                        resourceAccessor={(event) => (event as EventInfo).resourceID}
                        resourceIdAccessor={(resource) => (resource as Resource).resourceID}
                        resourceTitleAccessor={(resource) => (resource as Resource).resourceTitle}

                        onSelectSlot={calendarSelectSlotCallback}
                        onSelectEvent={calendarSelectEventCallback}
                        onEventDrop={calendarEventDropCallback}
                        onEventResize={calendarEventResizeCallback}
                        onDragStart={calendarEventDragStartCallback}
                        eventPropGetter={eventPropGetter}

                        draggableAccessor={() => true}
                        resizableAccessor={() => true}
                    />
                    {
                        isUpdatingBooking &&
                        <BookingCreateForm
                            startTime={startTime || dayjs().tz()}
                            endTime={endTime || dayjs().tz()}
                            bookingType={bookingType}
                            carInfo={carInfo}
                            locationInfo={locationInfo}
                            isUpdatingBooking={isUpdatingBooking}
                            toggleFormVisibility={toggleFormVisibility}
                            syncStateWithBookingForm={syncStateWithBookingForm}
                            goToBookingSummary={goToBookingSummary}
                            clearNewBooking={clearNewBooking}
                        />
                    }
                </div>
            )}
        </>
    );
};

export default CalendarView;
